import React from "react";
import { motion } from "framer-motion";
import { differentiators, stats, aboutMedia, company } from "../mock";

const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-[#F1F5F9]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          {/* Image column */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={aboutMedia.partnership}
                alt="Rampart Partners — partnership"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/40 via-transparent to-transparent" />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-8 -right-4 md:-right-8 bg-[#0A1128] text-white p-8 max-w-[240px] shadow-2xl">
              <p className="text-[#CFA660] text-[10px] uppercase tracking-[0.3em] mb-3">
                Our Promise
              </p>
              <p className="font-['Playfair_Display'] text-xl md:text-2xl leading-snug italic">
                "Total anonymity and confidence."
              </p>
              <p className="mt-4 text-white/70 text-xs font-['Outfit']">
                Independent advisory.
              </p>
            </div>
          </motion.div>

          {/* Text column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="lg:col-span-7"
          >
            <p
              data-testid="about-overline"
              className="text-xs md:text-sm uppercase tracking-[0.3em] font-semibold text-[#172554] mb-6"
            >
              — About Rampart
            </p>
            <h2
              data-testid="about-title"
              className="font-['Playfair_Display'] text-[#0A1128] text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-medium mb-8"
            >
              A firm built around <span className="italic">experience</span> and reputation.
            </h2>
            <div className="space-y-6 text-[#475569] text-base md:text-lg leading-relaxed font-['Outfit']">
              <p>
                We use our knowledge and experience from running our own
                companies to guide our clients through complex transactions to
                successful outcomes.
              </p>
            </div>

            {/* Differentiators */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10 mt-14">
              {differentiators.map((d, i) => {
                const Icon = d.icon;
                return (
                  <motion.div
                    key={d.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.1 * i }}
                    data-testid={`differentiator-${i}`}
                    className="flex gap-4"
                  >
                    <div className="w-10 h-10 flex-shrink-0 border border-[#0A1128] flex items-center justify-center">
                      <Icon className="w-4 h-4 text-[#0A1128]" />
                    </div>
                    <div>
                      <h4 className="font-['Playfair_Display'] text-[#0A1128] text-lg mb-1">
                        {d.title}
                      </h4>
                      <p className="text-[#475569] text-sm leading-relaxed font-['Outfit']">
                        {d.text}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.9, delay: 0.3 }}
          data-testid="stats-bar"
          className="mt-28 border-t border-b border-[#0A1128]/20 py-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4 text-center md:text-left">
            {stats.map((s, i) => (
              <div
                key={s.label}
                data-testid={`stat-${i}`}
                className={`${
                  i !== 0 ? "md:border-l md:border-[#0A1128]/15 md:pl-8" : ""
                }`}
              >
                <p className="font-['Playfair_Display'] text-[#0A1128] text-4xl md:text-5xl font-medium tracking-tight">
                  {s.value}
                </p>
                <p className="mt-3 text-[#475569] text-xs md:text-sm uppercase tracking-[0.18em] font-['Outfit']">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
